import React, { useEffect, useState } from 'react';

interface ProgressDialogProps {
  isOpen: boolean;
  message?: string;
  subMessage?: string;
  /**
   * Optional delay (in ms) before showing the dialog. Prevents flicker for fast operations.
   * @default 0
   */
  delay?: number;
  /**
   * Minimum time (in ms) to show the dialog once visible. Prevents flicker if operation completes quickly after showing.
   * @default 0
   */
  minShowTime?: number;
}

/**
 * CompactTaxiProgress is a visually engaging progress dialog featuring a taxi animation.
 * @param isOpen - Controls visibility
 * @param message - Main message
 * @param subMessage - Optional sub-message
 * @param delay - Optional delay before showing dialog
 * @param minShowTime - Minimum time to show dialog once visible
 */
const ProgressDialog: React.FC<ProgressDialogProps> = ({ isOpen, message = 'Processing...', subMessage, delay = 0, minShowTime = 5000 }) => {
  const [show, setShow] = useState(false);
  const [showStart, setShowStart] = useState<number | null>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    let minTimer: NodeJS.Timeout | undefined;
    if (isOpen) {
      if (delay > 0) {
        timer = setTimeout(() => {
          setShow(true);
          setShowStart(Date.now());
        }, delay);
      } else {
        setShow(true);
        setShowStart(Date.now());
      }
    } else {
      if (show && showStart !== null) {
        const elapsed = Date.now() - showStart;
        if (elapsed < minShowTime) {
          minTimer = setTimeout(() => setShow(false), minShowTime - elapsed);
        } else {
          setShow(false);
        }
      } else {
        setShow(false);
      }
    }
    return () => {
      if (timer) clearTimeout(timer);
      if (minTimer) clearTimeout(minTimer);
    };
  }, [isOpen, delay, minShowTime, show, showStart]);

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm" data-testid="progress-dialog-overlay">
      <div className="bg-background-paper p-8 rounded-xl shadow-lg flex flex-col items-center text-foreground" data-testid="progress-dialog-container">
        {/* Taxi Progress Indicator */}
        <div className="relative mb-6">
          {/* Taxi Body */}
          <div className="relative w-16 h-10 animate-bounce">
            {/* Main taxi body */}
            <div className="w-16 h-7 bg-yellow-400 rounded-lg border-2 border-yellow-500 relative overflow-hidden">
              {/* Taxi roof light */}
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-6 h-2 bg-black text-white text-xs flex items-center justify-center rounded-sm">
                <div className="text-xs font-bold" style={{ fontSize: '6px' }}>TAXI</div>
              </div>
              {/* Windshield */}
              <div className="absolute top-0 left-1 w-4 h-4 bg-blue-100 rounded-tl-md border-r border-gray-300"></div>
              <div className="absolute top-0 right-1 w-4 h-4 bg-blue-100 rounded-tr-md border-l border-gray-300"></div>
              {/* Side windows */}
              <div className="absolute top-1 left-6 w-3 h-3 bg-blue-100 border border-gray-300"></div>
              <div className="absolute top-1 right-6 w-3 h-3 bg-blue-100 border border-gray-300"></div>
              {/* Door handles */}
              <div className="absolute top-3 left-5 w-1 h-1 bg-gray-600 rounded-full"></div>
              <div className="absolute top-3 right-5 w-1 h-1 bg-gray-600 rounded-full"></div>
              {/* Headlight */}
              <div className="absolute top-2 -left-1 w-2 h-2 bg-yellow-100 rounded-full border border-yellow-300"></div>
              {/* Checkered pattern */}
              <div className="absolute top-4 left-2 flex space-x-1">
                <div className="w-1 h-1 bg-black"></div>
                <div className="w-1 h-1 bg-white"></div>
                <div className="w-1 h-1 bg-black"></div>
                <div className="w-1 h-1 bg-white"></div>
                <div className="w-1 h-1 bg-black"></div>
              </div>
            </div>
            {/* Taxi base */}
            <div className="w-16 h-3 bg-yellow-500 border-2 border-yellow-600 border-t-0 rounded-b-lg"></div>
            {/* Front Wheel */}
            <div className="absolute -bottom-1 left-2">
              <div className="w-4 h-4 bg-gray-800 rounded-full border border-gray-900 animate-spin" style={{ animationDuration: '0.2s', animationTimingFunction: 'linear' }}>
                <div className="absolute inset-0.5 bg-gray-600 rounded-full">
                  <div className="absolute inset-0.5 bg-gray-400 rounded-full relative">
                    <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 transform -translate-y-1/4"></div>
                    <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-800 transform -translate-x-1/4"></div>
                    <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-gray-800 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rear Wheel */}
            <div className="absolute -bottom-1 right-2">
              <div className="w-4 h-4 bg-gray-800 rounded-full border border-gray-900 animate-spin" style={{ animationDuration: '0.2s', animationTimingFunction: 'linear' }}>
                <div className="absolute inset-0.5 bg-gray-600 rounded-full">
                  <div className="absolute inset-0.5 bg-gray-400 rounded-full relative">
                    <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 transform -translate-y-1/4"></div>
                    <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-800 transform -translate-x-1/4"></div>
                    <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-gray-800 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Loading text */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-gray-600 text-sm font-medium animate-pulse">{message}</span>
          </div>
          {/* Optional progress dots */}
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 flex space-x-1">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
        {/* Sub-message */}
        {subMessage && <div className="text-sm text-foreground-muted mt-2">{subMessage}</div>}
      </div>
    </div>
  );
};

export default ProgressDialog;
